import { useCallback, useMemo, useRef, useState } from 'react';
import { useAuthStore } from '@stores/auth.store';
import { useMessage } from '@context/MessageProvider';
import { formatUnderscoreToSpace } from '@utils/formatText';
import { getAvatarColor } from '@utils/avatarColor';
import { resolveProfileImageUrl } from '@hooks/projects/detail/useProjectDetail';
import { ENV } from '@configs/env';
import {
    extractMeUser,
    modifyPassword,
    updateMeProfile,
    verifyPassword,
} from '@services/profile.service';

const getApiErrorMessage = (error, fallback) =>
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback;

/**
 * Profile page hook — identity, avatar upload, profile edit, password change, permissions modal.
 */
export const useProfilePage = () => {
    const user = useAuthStore((state) => state.user);
    const planKey = useAuthStore((state) => state.planKey);
    const permissions = useAuthStore((state) => state.permissions) || [];
    const applyMeProfile = useAuthStore((state) => state.applyMeProfile);

    const { showMessage } = useMessage();
    const avatarInputRef = useRef(null);

    const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
    const [permSearch, setPermSearch] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState({ name: '', email: '', phone: '' });

    const [avatarUploading, setAvatarUploading] = useState(false);
    const [avatarRevision, setAvatarRevision] = useState(0);
    const [profileSaving, setProfileSaving] = useState(false);

    const [passwordVerified, setPasswordVerified] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordVerifying, setPasswordVerifying] = useState(false);
    const [passwordSaving, setPasswordSaving] = useState(false);

    const profile = useMemo(() => {
        if (!user) return null;
        const primaryRole =
            user.role || (Array.isArray(user.roles) && user.roles.length ? user.roles[0] : 'User');

        const planLabel = (() => {
            if (!planKey) return 'Free';
            if (typeof planKey === 'string') {
                return planKey.charAt(0).toUpperCase() + planKey.slice(1).toLowerCase();
            }
            if (planKey && typeof planKey === 'object' && (planKey.name ?? planKey.key)) {
                return planKey.name ?? planKey.key;
            }
            if (typeof user.plan === 'string') return user.plan;
            if (user.plan && (user.plan.name ?? user.plan.key)) {
                return user.plan.name ?? user.plan.key;
            }
            return 'Free';
        })();

        const companyName =
            user.company?.name ??
            user.companyName ??
            user.tenant?.name ??
            user.tenantName ??
            '';

        const avatarPath =
            user.avatarUrl ?? user.avatar ?? user.profileImage ?? null;
        const resolvedAvatar = avatarPath ? resolveProfileImageUrl(avatarPath, ENV.API_URL) : null;
        const avatarUrl =
            resolvedAvatar && avatarRevision > 0
                ? `${resolvedAvatar}${resolvedAvatar.includes('?') ? '&' : '?'}v=${avatarRevision}`
                : resolvedAvatar;
        const initials = (user.name || user.email || 'U')
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);

        return {
            name: user.name || user.email?.split('@')[0] || 'User',
            email: user.email || '',
            phone: user.phone || '',
            role: formatUnderscoreToSpace(primaryRole || 'User'),
            roleRaw: primaryRole || 'User',
            planLabel,
            companyName,
            isClient: !!user.isClient,
            isSuperAdmin: !!user.isSuperAdmin,
            verified: !!user.verified,
            isOnboarded: !!user.isOnboarded,
            companyId: user.companyId || null,
            publicId: user.publicId || null,
            tenantId: user.tenantId || null,
            avatarUrl,
            avatarCacheKey: `${user.avatarUrl ?? user.avatar ?? user.profileImage ?? ''}-${avatarRevision}`,
            avatarBg: getAvatarColor(user.id || user.email || user.name),
            initials,
        };
    }, [user, planKey, avatarRevision]);

    const copyToClipboard = useCallback(
        (value, label) => {
            if (!value) return;
            navigator?.clipboard?.writeText(String(value)).then(
                () => showMessage('success', `${label} copied to clipboard.`),
                () => showMessage('error', 'Could not copy to clipboard.'),
            );
        },
        [showMessage],
    );

    const { permissionGroups, totalModules, totalFeatures, totalActions } = useMemo(() => {
        const groupsMap = new Map();
        const allActionsSet = new Set();
        const featuresSet = new Set();

        for (const key of permissions) {
            if (typeof key !== 'string') continue;
            const parts = key.split('.');
            if (!parts.length) continue;

            const module = parts[0] || 'other';
            const feature = parts[1] || 'general';
            const action = parts[2] || parts[1] || 'all';

            const groupKey = `${module}.${feature}`;
            if (!groupsMap.has(groupKey)) {
                groupsMap.set(groupKey, {
                    module,
                    feature,
                    actions: new Set(),
                });
            }

            const group = groupsMap.get(groupKey);
            group.actions.add(action);
            allActionsSet.add(`${module}.${feature}.${action}`);
            featuresSet.add(`${module}.${feature}`);
        }

        const permissionGroups = Array.from(groupsMap.values())
            .map((g) => ({
                module: g.module,
                feature: g.feature,
                actions: Array.from(g.actions).sort(),
            }))
            .sort((a, b) => {
                if (a.module === b.module) return a.feature.localeCompare(b.feature);
                return a.module.localeCompare(b.module);
            });

        return {
            permissionGroups,
            totalModules: new Set(permissionGroups.map((g) => g.module)).size,
            totalFeatures: featuresSet.size,
            totalActions: allActionsSet.size,
        };
    }, [permissions]);

    const permissionTableRows = useMemo(() => {
        const rows = permissionGroups.flatMap((group) =>
            group.actions.map((action) => ({
                key: `${group.module}.${group.feature}.${action}`,
                module: formatUnderscoreToSpace(group.module),
                feature: formatUnderscoreToSpace(group.feature),
                action: formatUnderscoreToSpace(action),
                searchBlob: `${group.module} ${group.feature} ${action}`.toLowerCase(),
            })),
        );

        const q = permSearch.trim().toLowerCase();
        if (!q) return rows;

        return rows.filter(
            (row) =>
                row.searchBlob.includes(q) ||
                row.module.toLowerCase().includes(q) ||
                row.feature.toLowerCase().includes(q) ||
                row.action.toLowerCase().includes(q),
        );
    }, [permissionGroups, permSearch]);

    const syncDraftFromProfile = useCallback(() => {
        if (!profile) return;
        setDraft({
            name: profile.name,
            email: profile.email,
            phone: profile.phone,
        });
    }, [profile]);

    const openPermissionsModal = useCallback(() => {
        setPermSearch('');
        setPermissionsModalOpen(true);
    }, []);

    const closePermissionsModal = useCallback(() => {
        setPermissionsModalOpen(false);
        setPermSearch('');
    }, []);

    const startEditing = useCallback(() => {
        syncDraftFromProfile();
        setIsEditing(true);
    }, [syncDraftFromProfile]);

    const cancelEditing = useCallback(() => {
        syncDraftFromProfile();
        setIsEditing(false);
    }, [syncDraftFromProfile]);

    const updateDraftField = useCallback((field, value) => {
        setDraft((prev) => ({ ...prev, [field]: value }));
    }, []);

    const openAvatarPicker = useCallback(() => {
        if (avatarUploading) return;
        avatarInputRef.current?.click();
    }, [avatarUploading]);

    const handleAvatarFileChange = useCallback(
        async (event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (!file || !file.type.startsWith('image/')) {
                showMessage('error', 'Please choose a valid image file.');
                return;
            }

            setAvatarUploading(true);
            try {
                const responseData = await updateMeProfile({ avatarFile: file });
                const updatedUser = extractMeUser(responseData);
                if (updatedUser) {
                    applyMeProfile(responseData);
                } else {
                    await useAuthStore.getState().refreshMe();
                }
                setAvatarRevision((n) => n + 1);
                showMessage('success', 'Profile photo updated.');
            } catch (error) {
                showMessage('error', getApiErrorMessage(error, 'Failed to update profile photo.'));
            } finally {
                setAvatarUploading(false);
            }
        },
        [applyMeProfile, showMessage],
    );

    const saveProfile = useCallback(async () => {
        if (!profile) return;

        const payload = {};
        const trimmedName = draft.name?.trim();
        const trimmedEmail = draft.email?.trim();
        const trimmedPhone = draft.phone?.trim();

        if (trimmedName && trimmedName !== profile.name) payload.name = trimmedName;
        if (trimmedEmail && trimmedEmail !== profile.email) payload.email = trimmedEmail;
        if (trimmedPhone !== (profile.phone || '')) payload.phone = trimmedPhone;

        if (Object.keys(payload).length === 0) {
            showMessage('info', 'No changes to save.');
            setIsEditing(false);
            return;
        }

        setProfileSaving(true);
        try {
            const responseData = await updateMeProfile(payload);
            const updatedUser = extractMeUser(responseData);
            if (updatedUser) {
                applyMeProfile(responseData);
            } else {
                await useAuthStore.getState().refreshMe();
            }
            showMessage('success', 'Profile updated successfully.');
            setIsEditing(false);
        } catch (error) {
            showMessage('error', getApiErrorMessage(error, 'Failed to update profile.'));
        } finally {
            setProfileSaving(false);
        }
    }, [applyMeProfile, draft, profile, showMessage]);

    const handleVerifyPassword = useCallback(async () => {
        if (!currentPassword.trim()) {
            showMessage('error', 'Enter your current password.');
            return;
        }

        setPasswordVerifying(true);
        try {
            await verifyPassword(currentPassword);
            setPasswordVerified(true);
            showMessage('success', 'Password verified. You can set a new password.');
        } catch (error) {
            setPasswordVerified(false);
            showMessage('error', getApiErrorMessage(error, 'Current password is incorrect.'));
        } finally {
            setPasswordVerifying(false);
        }
    }, [currentPassword, showMessage]);

    const resetPasswordForm = useCallback(() => {
        setPasswordVerified(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
    }, []);

    const handleModifyPassword = useCallback(async () => {
        if (!passwordVerified) return;

        if (!newPassword || newPassword.length < 6) {
            showMessage('error', 'New password must be at least 6 characters.');
            return;
        }
        if (newPassword !== confirmPassword) {
            showMessage('error', "Passwords don't match.");
            return;
        }

        setPasswordSaving(true);
        try {
            await modifyPassword({
                currentPassword,
                newPassword,
            });
            showMessage('success', 'Password updated successfully.');
            resetPasswordForm();
        } catch (error) {
            showMessage('error', getApiErrorMessage(error, 'Failed to update password.'));
        } finally {
            setPasswordSaving(false);
        }
    }, [confirmPassword, currentPassword, newPassword, passwordVerified, resetPasswordForm, showMessage]);

    return {
        profile,
        permissions,
        permissionGroups,
        permissionTableRows,
        totalModules,
        totalFeatures,
        totalActions,
        permissionsModalOpen,
        permSearch,
        setPermSearch,
        openPermissionsModal,
        closePermissionsModal,
        isEditing,
        draft,
        updateDraftField,
        startEditing,
        cancelEditing,
        saveProfile,
        profileSaving,
        avatarInputRef,
        avatarUploading,
        openAvatarPicker,
        handleAvatarFileChange,
        passwordVerified,
        currentPassword,
        setCurrentPassword,
        newPassword,
        setNewPassword,
        confirmPassword,
        setConfirmPassword,
        passwordVerifying,
        passwordSaving,
        handleVerifyPassword,
        handleModifyPassword,
        resetPasswordForm,
        copyToClipboard,
    };
};
